import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { capturePosthogEvent } from '../services/posthogClient';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';
export type PWADeviceType = 'ios' | 'android' | 'desktop' | 'unknown';
export type IOSInstallDevice = 'iphone' | 'ipad' | 'ios' | null;

interface PWAInstallContextValue {
  canInstall: boolean;
  promptInstall: () => Promise<InstallOutcome>;
  deviceType: PWADeviceType;
  iosInstallDevice: IOSInstallDevice;
  isStandalone: boolean;
}

const PWAInstallContext = createContext<PWAInstallContextValue | null>(null);

const isStandaloneDisplayMode = (): boolean => {
  if (typeof window === 'undefined') return false;

  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;

  return iosStandalone || displayModeStandalone;
};

const detectDeviceType = (): PWADeviceType => {
  if (typeof window === 'undefined') return 'unknown';

  const userAgent = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

  if (isIOS) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  if (/Windows|Macintosh|Linux/i.test(userAgent)) return 'desktop';

  return 'unknown';
};

const detectIOSInstallDevice = (): IOSInstallDevice => {
  if (typeof window === 'undefined') return null;

  const userAgent = window.navigator.userAgent || '';
  const isTouchMac = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;

  if (/iPad/.test(userAgent) || isTouchMac) return 'ipad';
  if (/iPhone|iPod/.test(userAgent)) return 'iphone';

  const isIOS = /iPad|iPhone|iPod/.test(userAgent) || isTouchMac;
  return isIOS ? 'ios' : null;
};

export const PWAInstallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [deviceType] = useState<PWADeviceType>(() => detectDeviceType());
  const [iosInstallDevice] = useState<IOSInstallDevice>(() => detectIOSInstallDevice());
  const [isStandalone, setIsStandalone] = useState<boolean>(() => isStandaloneDisplayMode());

  useEffect(() => {
    const handleDisplayModeChange = () => {
      setIsStandalone(isStandaloneDisplayMode());
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredPromptRef.current = event as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      capturePosthogEvent('pwa_installed', { device_type: deviceType });
      deferredPromptRef.current = null;
      setCanInstall(false);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('visibilitychange', handleDisplayModeChange);

    const displayModeQuery = window.matchMedia('(display-mode: standalone)');
    if (displayModeQuery.addEventListener) {
      displayModeQuery.addEventListener('change', handleDisplayModeChange);
    } else if (displayModeQuery.addListener) {
      displayModeQuery.addListener(handleDisplayModeChange);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('visibilitychange', handleDisplayModeChange);

      if (displayModeQuery.removeEventListener) {
        displayModeQuery.removeEventListener('change', handleDisplayModeChange);
      } else if (displayModeQuery.removeListener) {
        displayModeQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, [deviceType]);

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    const deferredPrompt = deferredPromptRef.current;
    if (!deferredPrompt) return 'unavailable';

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    deferredPromptRef.current = null;
    setCanInstall(false);

    capturePosthogEvent('pwa_install_prompt_outcome', {
      outcome,
      device_type: deviceType
    });

    return outcome;
  }, [deviceType]);

  const value = useMemo<PWAInstallContextValue>(() => ({
    canInstall: canInstall && !isStandalone,
    promptInstall,
    deviceType,
    iosInstallDevice,
    isStandalone
  }), [canInstall, deviceType, iosInstallDevice, isStandalone, promptInstall]);

  return (
    <PWAInstallContext.Provider value={value}>
      {children}
    </PWAInstallContext.Provider>
  );
};

export const usePWAInstallContext = (): PWAInstallContextValue => {
  const context = useContext(PWAInstallContext);
  if (context) {
    return context;
  }

  return {
    canInstall: false,
    promptInstall: async () => 'unavailable',
    deviceType: 'unknown',
    iosInstallDevice: null,
    isStandalone: false
  };
};
